import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Check, X, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-600",
};

export default function EnterpriseSupplierRequests() {
  const [requests, setRequests] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/supplier-requests").then((r) => setRequests(r.data.requests)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const resolve = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/supplier-requests/${id}/resolve`, { action });
      toast.success(action === "approve" ? "Supplier approved and added to your network" : "Seller addition request rejected");
      setDetail(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };

  if (!requests) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const pending = requests.filter((r) => r.status === "pending");

  return (
    <div className="space-y-6" data-testid="enterprise-supplier-requests-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Supplier Requests</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Buyers must get Enterprise approval before a seller joins your approved supplier network.
          {pending.length > 0 && <span className="ml-1 font-semibold text-amber-700">{pending.length} pending</span>}
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-white" data-testid="supplier-requests-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Seller</th>
              <th className="px-4 py-3 font-medium">Requested By</th>
              <th className="px-4 py-3 font-medium">GST</th>
              <th className="px-4 py-3 font-medium">Request Date</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground" data-testid="supplier-requests-empty">No supplier addition requests yet.</td></tr>
            )}
            {requests.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`supplier-request-row-${r.id.slice(0, 8)}`}>
                <td className="px-4 py-3">
                  <p className="font-medium">{r.supplier_name}</p>
                  <p className="text-xs text-muted-foreground">{r.supplier_email}</p>
                </td>
                <td className="px-4 py-3">{r.buyer_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.gst_number || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{fmtDateTime(r.created_at)}</td>
                <td className="px-4 py-3">
                  <span data-testid={`supplier-request-status-${r.id.slice(0, 8)}`} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status]}`}>
                    {r.status === "pending" ? "Pending" : r.status}
                  </span>
                  {r.resolved_by && <p className="mt-0.5 text-[10px] text-muted-foreground">by {r.resolved_by}</p>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" data-testid={`supplier-request-view-${r.id.slice(0, 8)}`} onClick={() => setDetail(r)}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> View Supplier
                    </Button>
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" data-testid={`supplier-request-approve-${r.id.slice(0, 8)}`} disabled={!!busy}
                          className="bg-[#166534] hover:bg-[#14532D]" onClick={() => resolve(r.id, "approve")}>
                          {busy === r.id + "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Approve
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`supplier-request-reject-${r.id.slice(0, 8)}`} disabled={!!busy}
                          className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => resolve(r.id, "reject")}>
                          <X className="mr-1 h-3.5 w-3.5" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="supplier-request-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading">Supplier Details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm" data-testid="supplier-request-detail">
              {[
                ["Company", detail.supplier_name], ["Contact Person", detail.contact_person || "—"],
                ["Email", detail.supplier_email], ["Phone", detail.phone || "—"], ["City", detail.city || "—"],
                ["GST", detail.gst_number || "—"], ["Requested By", detail.buyer_name],
                ["Reason / Comment", detail.reason || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b py-1.5 last:border-0">
                  <span className="text-muted-foreground">{k}</span><span className="text-right font-medium">{v}</span>
                </div>
              ))}
              {detail.status === "pending" && (
                <div className="flex gap-2 pt-3">
                  <Button data-testid="detail-approve-btn" disabled={!!busy} className="flex-1 bg-[#166534] hover:bg-[#14532D]" onClick={() => resolve(detail.id, "approve")}>Approve</Button>
                  <Button data-testid="detail-reject-btn" variant="outline" disabled={!!busy} className="flex-1 border-red-200 text-red-700 hover:bg-red-50" onClick={() => resolve(detail.id, "reject")}>Reject</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
